import { clearStorage } from '@getjust/commons';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ReshapedUser } from '$src/types';
import type { CheckoutV1 } from '@getjust/api-schema';

import { useCustomRouter } from '$hooks/client';
import { useShop } from '$hooks/queries';
import { useCountryCodeAtom, useEmailAtom } from '$hooks/state';
import { USER_INFO_QUERY_KEY } from '$src/constants';
import { AuthStatuses, useAuth } from '$src/hooks/auth';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { justAPI } from '$src/http';
import { posthog } from '$src/utils';

export const useUserInfo = () => {
  const { authState, setIsAuthenticated } = useAuth() ?? {};
  const router = useCustomRouter();
  const { data: shop } = useShop();
  const { changeCountry } = useCountryCodeAtom();
  const { onEmailChange } = useEmailAtom();
  const { identifyUser, track } = useTrackAnalytics();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [USER_INFO_QUERY_KEY],
    queryFn: async () => {
      try {
        const response = await justAPI.get<CheckoutV1.AuthMeResponseBody>('/v1/auth/me');

        // Defensive check for malformed responses (CORS issues, network problems)
        // Backend always returns email, so if missing it's a transport issue
        if (!response.data || typeof response.data !== 'object' || !response.data.email) {
          console.error('[useUserInfo] Invalid response received - likely network/CORS issue:', {
            hasData: Boolean(response.data),
            dataType: typeof response.data,
            hasEmail: Boolean((response.data as any)?.email),
            status: response?.status,
          });
          track(AnalyticsEventName.EMPTY_AUTH_RESPONSE);

          // Remove the token from local storage, clear quertClient cache and redirect to unauthenticated page
          // to start a new fresh session
          clearStorage('localStorage', 'token');
          queryClient.clear();
          setIsAuthenticated?.(false);

          return;
        }

        const reshaped = reshapeCurrentUserData(response.data);
        changeCountry(reshaped?.address?.country);
        onEmailChange(reshaped?.email);

        const currentDistinctId = posthog.getDistinctId();

        identifyUser(reshaped.id, {
          pspCustomerId: reshaped.pspCustomerId,
          just_customer_id: reshaped.id,
        });

        if (currentDistinctId && currentDistinctId !== reshaped.id) {
          posthog.aliasUser(currentDistinctId, reshaped.id);
        }

        return reshaped;
      } catch (error: any) {
        if (error?.type === 'RequestError') {
          switch (error.status) {
            case 404:
              router.push('/unauthenticated');
              return;
            default:
              return;
          }
        }
        return;
      }
    },
    enabled: authState === AuthStatuses.AUTHENTICATED && Boolean(shop),
  });
};

export function reshapeCurrentUserData(data: CheckoutV1.AuthMeResponseBody): ReshapedUser {
  const address = data?.addresses?.find((address) => address.is_default_shipping);
  const billing_address = data?.addresses?.find((address) => address.is_default_billing);
  const formattedSources = (data.sources ?? []).flatMap((source) => {
    return source.card
      ? {
          id: source.id,
          is_default: !!source.is_default,
          ...source.card,
          last4: source.card.last_4, // @TODO should be sent directly like this by backend
        }
      : [];
  });

  return {
    email: data.email,
    pspCustomerId: data.customer_id,
    id: data.id,
    verifiedPhone: data.verifiedPhone,
    user: {
      lastname: data?.personal_informations?.last_name,
      firstname: data?.personal_informations?.first_name,
      phone_number: data?.phone,
    },
    address: address
      ? {
          ...address?.address,
          id: address?.id,
        }
      : undefined,

    billing_address: billing_address
      ? {
          ...billing_address?.address,
          id: billing_address?.id,
        }
      : undefined,
    sources: {
      cards: formattedSources,
      selected: formattedSources?.find((source) => source.is_default),
    },
    addresses:
      data?.addresses?.map(({ address, ...rest }) => ({
        ...address,
        ...rest,
      })) ?? [],
  };
}
